import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Mail } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { OTP_CODE_LENGTH, isCompleteOtpCode, normalizeOtpCode } from '@/lib/otp';
import { BrandGlyph } from '@/components/BrandGlyph';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { hasSupabaseConfig } from '@/lib/auth/supabase';
import { canShowSkip, getAuthState } from '@/lib/auth/auth-state';
import { clearPendingDiscard, setGuestMarker, setPendingDiscard } from '@/lib/auth/guest-marker';
import { signIn, useSession, verifyEmailOtp } from '@/lib/auth';
import { useStore } from '@/lib/store';
import { useT } from '@/lib/i18n';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

// guest 有数据时的强制二选一（设计 v4 §5/§6）：共享设备上静默合并是错误的隐私默认值。
// 「不保留」只写 pendingDiscard 标志（延迟执行，engine 在登录后消费）—— 魔法链接可能
// 永远不被点击，立即删 = 没登上号数据也没了。
type MergeChoice = 'merge' | 'discard' | null;

function LoginPage() {
  const t = useT();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [choice, setChoice] = useState<MergeChoice>(null);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [codeError, setCodeError] = useState('');
  const navigate = useNavigate();
  const supabaseMode = hasSupabaseConfig();
  const session = useSession();
  const taskCount = useStore((s) => s.tasks.length);

  // 每次渲染现算（登录页无并发写这些状态的路径；避免多一份易腐缓存）
  const authState = getAuthState();
  const guestWithData = authState === 'guest' && taskCount > 0;
  const showSkip = useMemo(() => canShowSkip(), []);
  const choiceMissing = guestWithData && choice === null;

  // Tauri：deep-link 回调把 token 喂给 supabase 后，session 才更新；这时候要主动跳走，
  // 因为我们已经在 /login 上，beforeLoad 不会再被触发。浏览器场景也兼容。
  useEffect(() => {
    if (session) {
      navigate({ to: '/today' });
    }
  }, [session, navigate]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || choiceMissing) return;
    // 陈旧 flag 不许跨会话存活（v4.1-1）：每次提交先清，再按本次选择写入
    clearPendingDiscard();
    if (guestWithData && choice === 'discard') {
      // 破坏性二次确认（guardrails §F）——flag 写入后登录成功即不可逆删除本地数据
      const ok = window.confirm(t('login.merge.discardConfirmBody', { count: taskCount }));
      if (!ok) return;
      setPendingDiscard();
    }
    setState('sending');
    setErrorMsg('');
    try {
      await signIn(email);
      setState('sent');
      if (!supabaseMode) {
        setTimeout(() => navigate({ to: '/today' }), 600);
      }
    } catch (err) {
      setState('error');
      setErrorMsg(err instanceof Error ? err.message : t('login.error'));
    }
  };

  const onSkip = () => {
    if (!setGuestMarker()) {
      setState('error');
      setErrorMsg(t('login.skipFailed'));
      return;
    }
    navigate({ to: '/today' });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-surface)] px-4">
      <div className="w-full max-w-sm rounded-xl border bg-card p-8 shadow-sm">
        <div className="mb-6 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <BrandGlyph className="h-5 w-5" />
          </div>
          <span className="text-base font-semibold">{t('brand.name')}</span>
        </div>
        <h1 className="text-xl font-semibold">{t('login.welcomeBack')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {supabaseMode ? t('login.helperSupabase') : t('login.helperLocal')}
        </p>

        {state === 'sent' ? (
          <div className="mt-6 space-y-3">
            <div className="rounded-lg border bg-muted/40 p-4 text-sm">
              <Mail className="mb-2 h-5 w-5 text-primary" />
              {supabaseMode ? t('login.sentSupabase', { email }) : t('login.sentLocal', { email })}
            </div>

            {/* 验证码兜底路径（与 mobile LoginScreen parity）：邮件里的链接在企业邮箱
              重写、异设备收信等情况下会失效，输码不依赖链接。归一化（空格 / 全角数字）
              走 mobile 同款纯函数的 web 副本，见 lib/otp.ts。 */}
            {supabaseMode ? (
              <form
                className="space-y-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!isCompleteOtpCode(code) || verifying) return;
                  setVerifying(true);
                  setCodeError('');
                  verifyEmailOtp(email, code)
                    .catch(() => setCodeError(t('login.otpFailed')))
                    .finally(() => setVerifying(false));
                }}
              >
                <p className="text-sm text-muted-foreground">{t('login.otpPrompt')}</p>
                <div className="flex gap-2">
                  <Input
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={OTP_CODE_LENGTH}
                    placeholder={t('login.otpPlaceholder')}
                    value={code}
                    onChange={(e) => {
                      setCode(normalizeOtpCode(e.target.value));
                      setCodeError('');
                    }}
                    disabled={verifying}
                  />
                  <Button type="submit" disabled={!isCompleteOtpCode(code) || verifying}>
                    {t('login.otpSubmit')}
                  </Button>
                </div>
                {codeError ? <p className="text-sm text-destructive">{codeError}</p> : null}
              </form>
            ) : null}
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 space-y-3">
            <Input
              type="email"
              placeholder={t('login.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />

            {guestWithData ? (
              <fieldset className="space-y-2 rounded-lg border bg-muted/30 p-3">
                <legend className="px-1 text-xs font-medium text-muted-foreground">
                  {t('login.merge.title', { count: taskCount })}
                </legend>
                <label className="flex cursor-pointer items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name="guest-merge"
                    checked={choice === 'merge'}
                    onChange={() => setChoice('merge')}
                    className="mt-0.5"
                  />
                  <span>
                    {t('login.merge.keep')}
                    <span className="block text-xs text-muted-foreground">
                      {t('login.merge.irreversible')}
                    </span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name="guest-merge"
                    checked={choice === 'discard'}
                    onChange={() => setChoice('discard')}
                    className="mt-0.5"
                  />
                  <span>
                    {t('login.merge.discard')}
                    {choice === 'discard' ? (
                      <span className="block text-xs text-destructive">
                        {t('login.merge.discardConfirmTitle', { count: taskCount })}
                      </span>
                    ) : null}
                  </span>
                </label>
              </fieldset>
            ) : null}

            <Button
              type="submit"
              className="w-full"
              disabled={state === 'sending' || choiceMissing}
            >
              {state === 'sending' ? t('login.sendingLink') : t('login.sendLink')}
            </Button>
            {state === 'error' ? <p className="text-sm text-destructive">{errorMsg}</p> : null}
          </form>
        )}

        {state !== 'sent' && showSkip ? (
          <Button
            type="button"
            variant="ghost"
            className="mt-3 w-full text-muted-foreground"
            onClick={onSkip}
          >
            {t('login.skipGuest')}
          </Button>
        ) : null}
        {authState === 'guest' && !guestWithData && state !== 'sent' ? (
          <p className="mt-3 text-center text-xs text-muted-foreground">
            {t('guest.storageWarning')}
          </p>
        ) : null}

        {!supabaseMode && (
          <p className="mt-6 text-center text-xs text-muted-foreground">{t('login.modeLocal')}</p>
        )}
      </div>
    </div>
  );
}
